import React, { useContext } from 'react';
import { Link } from 'react-router-dom';
import { Highlight } from 'react-instantsearch';
import { countFormat, HorizontalPreload } from './template';
import { BasketContext } from './context-basket';

import logo48ab from 'url:./assets/logo-48-ab.png';
import logo48mas from 'url:./assets/logo-48-mas.png';
import logo48mm from 'url:./assets/logo-48-mm.png';
import logo48mi from 'url:./assets/logo-48-mi.png';
import logo48skl from 'url:./assets/logo-48-skl.png';
import logo48ef from 'url:./assets/logo-48-ef.png';
import logo48pns from 'url:./assets/logo-48-pns.png';
import logo48krt from 'url:./assets/logo-48-krt.png';
import logo48bzr from 'url:./assets/logo-48-bzr.png';
import logo48thn from 'url:./assets/logo-48-thn.jpg';
import logo48xlk from 'url:./assets/logo-48-xlk.png';
import logo48ldl from 'url:./assets/logo-48-ldl.png';
import logo48glx from 'url:./assets/logo-48-glx.png';

export const marketInfo = {
  ab: { market: 'ab', logo: logo48ab, alt: 'ΑΒ' },
  mas: { market: 'mas', logo: logo48mas, alt: 'Μασούτης' },
  mm: { market: 'mm', logo: logo48mm, alt: 'My Market' },
  mi: { market: 'mi', logo: logo48mi, alt: 'Market In' },
  skl: { market: 'skl', logo: logo48skl, alt: 'Σκλαβενίτης' },
  ef: { market: 'ef', logo: logo48ef, alt: 'e-Fresh.gr' },
  pns: { market: 'pns', logo: logo48pns, alt: 'Panes.gr' },
  krt: { market: 'krt', logo: logo48krt, alt: 'Κρητικός' },
  bzr: { market: 'bzr', logo: logo48bzr, alt: 'Bazaar' },
  thn: { market: 'thn', logo: logo48thn, alt: 'Θανόπουλος' },
  xlk: { market: 'xlk', logo: logo48xlk, alt: 'Χαλκιαδάκης' },
  ldl: { market: 'ldl', logo: logo48ldl, alt: 'Lidl' },
  glx: { market: 'glx', logo: logo48glx, alt: 'Γαλαξίας' },
};

const marketRedirectBaseURL =
  'https://88ibmzhp62.execute-api.eu-west-1.amazonaws.com/prod/v1/market-redirect';
const photosURL = 'https://resources.zuper-market.gr/photos/';

import noPhoto from 'url:./assets/no-photo.png';
export { noPhoto };

export function MarketIcon({ market }) {
  const m = marketInfo[market];
  if (m === undefined) {
    return null;
  }
  return (
    <img
      className="object-contain w-6 h-6"
      src={m?.logo}
      alt={m?.alt}
      title={m?.alt}
    />
  );
}

export function marketTitle({ market }) {
  const m = marketInfo[market];
  return m?.alt;
}

export function marketRedirectURL(hit) {
  return marketRedirectBaseURL + '?url=' + encodeURIComponent(hit.url);
}

export function kindOf(hit) {
  return hit?.kind || 'product';
}

export function isMultiMarketCluster(hit) {
  return kindOf(hit) === 'cluster' && hit['member-count'] > 1;
}

export function internalURL(hit) {
  if (kindOf(hit) === 'cluster') {
    const rawId = String(hit.id).replace(/^cluster-/, '');
    return `/cluster/${rawId}/${hit.slug}`;
  }
  return `/product/${hit.id}/${hit.slug}`;
}

export function MarketIconsRow({ markets, max = 4 }) {
  if (!markets || markets.length === 0) return null;
  const shown = markets.slice(0, max);
  const overflow = markets.length - shown.length;

  return (
    <div className="flex items-center gap-1">
      {shown.map((market) => (
        <MarketIcon key={market} market={market} />
      ))}
      {overflow > 0 && (
        <span className="text-xs text-gray-500 bg-gray-100 rounded-full w-5 h-5 flex items-center justify-center">
          +{overflow}
        </span>
      )}
    </div>
  );
}

export function photoURL(hit) {
  if (hit?.['photo-filename']) return photosURL.concat(hit?.['photo-filename']);
  else return noPhoto;
}

let priceFormat = new Intl.NumberFormat('el-EL', {
  style: 'currency',
  currency: 'EUR',
  minimumFractionDigits: 2,
  maximumFractionDigits: 4,
});

export function formatPrice({ price }) {
  return priceFormat.format(price);
}

const unitLabels = {
  dose: 'δόση',
  kg: 'κιλό',
  lt: 'λίτρο',
  m: 'μ',
  m2: 'τ.μ.',
  mezoura: 'μεζούρα',
  pair: 'ζεύγος',
  piece: 'τεμ',
  sheet: 'φύλλο',
  wash: 'πλύση',
};

export function formatUnit(unit) {
  let label = unitLabels[unit];
  if (label === undefined) {
    return unit;
  } else {
    return label;
  }
}

export function formatDuration(seconds) {
  const days = Math.floor(seconds / 86400);
  seconds -= days * 86400;

  const hours = Math.floor(seconds / 3600);
  seconds -= hours * 3600;

  const minutes = Math.floor(seconds / 60);
  seconds -= minutes * 60;

  let result = '';
  if (days) result += `${days} ${days !== 1 ? 'μέρες' : 'μέρα'}`;
  else if (hours) result += `${hours} ${hours !== 1 ? 'ώρες' : 'ώρα'}`;
  else if (minutes) result += `${minutes} ${minutes !== 1 ? 'λεπτά' : 'λεπτό'}`;

  return result.trim();
}

export function formatPriceHistoryDate(dateString) {
  const date = new Date(dateString);
  const options = { day: 'numeric', month: 'short' };
  const formatter = new Intl.DateTimeFormat('el-GR', options);
  return formatter.format(date);
}

const dateFormatterYYYYMMDD = new Intl.DateTimeFormat('en-CA', {
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
});

export function formatDateToYYYYMMDD(date) {
  return dateFormatterYYYYMMDD.format(date);
}

export function effectivePrice(p) {
  return p?.prices?.['per-item']?.scalar;
}

function QuantityButton({ onClick, pos, children, ...props }) {
  var style = '';

  switch (pos) {
    case 'left':
      style = 'rounded-tl-full rounded-bl-full';
      break;
    case 'right':
      style = 'rounded-tr-full rounded-br-full';
      break;
    default:
      style = 'rounded-full';
  }

  return (
    <span className="flex items-center justify-center rounded-full cursor-pointer select-none">
      <a
        className={
          style +
          ' flex items-center justify-center bg-gray-300 hover:bg-gray-400 text-sm text-black h-8 w-8 font-extrabold'
        }
        onClick={(event) => {
          event.preventDefault();
          onClick(event);
        }}
        {...props}
      >
        {children}
      </a>
    </span>
  );
}

export function QuantityWidget({ addFun, removeFun, quantity }) {
  return (
    <div className="flex items-stretch">
      {quantity ? (
        <>
          <QuantityButton pos="left" onClick={removeFun}>
            -
          </QuantityButton>
          <div className="border-gray-300 border-t-2 border-b-2 w-8 h-8 text-center flex items-center justify-center text-sm leading-none">
            {quantity}
          </div>
          <QuantityButton pos="right" onClick={addFun}>
            +
          </QuantityButton>
        </>
      ) : (
        <span className="flex items-center justify-center rounded-full cursor-pointer select-none">
          <a
            className={
              'rounded-full flex items-center justify-center bg-gray-300 hover:bg-gray-400 text-sm text-black h-8 px-4'
            }
            onClick={addFun}
          >
            <span className="hidden sm:block">Προσθήκη</span>
            <span className="block sm:hidden">+</span>
          </a>
        </span>
      )}
    </div>
  );
}

export function PriceTag({
  product,
  boldPerItem = true,
  loading,
  centerUsualPrice = false,
  showFromPrefix = false,
}) {
  const currentPrice = product.prices?.['per-item']?.scalar;
  const usualPrice = product.prices?.usual;
  const percentageChange = product.prices?.['percentage-change'] || 0;
  const shouldShowChange = Math.abs(percentageChange) >= 2;
  const isIncrease = percentageChange > 0;

  return (
    <div className=" text-gray-800">
      {loading ? (
        <HorizontalPreload />
      ) : (
        <>
          <span className={boldPerItem ? 'font-bold' : ''}>
            {showFromPrefix && 'από '}
            {formatPrice({ price: currentPrice })}
          </span>
          <span className="pl-2 text-gray-600 text-sm">
            ({formatPrice({ price: product.prices?.['per-unit']?.scalar })}/
            {formatUnit(product.prices?.['per-unit']?.unit)})
          </span>
          {usualPrice && (
            <div
              className={`flex items-center gap-2 mt-2 ${centerUsualPrice ? 'justify-center' : ''}`}
            >
              <span className="text-gray-500 text-sm">
                Συνήθως {formatPrice({ price: usualPrice })}
              </span>
              {shouldShowChange && (
                <span
                  className={`text-sm px-2 py-1 rounded-full font-semibold ${
                    isIncrease
                      ? 'bg-red-100 text-red-700'
                      : 'bg-green-600 text-white'
                  }`}
                >
                  {isIncrease ? '+' : '-'}
                  {Math.abs(percentageChange)}%
                </span>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}

export function DesktopHit({ hit, useHighlight = true }) {
  const basket = useContext(BasketContext);
  const isCluster = isMultiMarketCluster(hit);

  return (
    <div className="border rounded-lg p-1 min-h-full text-center hover:border-gray-400 hover:shadow-lg grid grid-cols-1">
      <div className="justify-self-end">
        {isCluster ? (
          <MarketIconsRow markets={hit.markets} />
        ) : (
          <MarketIcon market={hit.market} />
        )}
      </div>

      <Link to={internalURL(hit)} className="flex justify-center items-center">
        <img
          className="object-contain w-32 h-32"
          src={photoURL(hit)}
          onError={(e) => {
            e.target.src = noPhoto;
          }}
        />
      </Link>

      <div className="text-gray-700 text-sm md:min-h-[5rem] px-2 pt-3">
        <Link to={internalURL(hit)} className="hover:underline">
          {useHighlight ? (
            <Highlight
              attribute="title"
              hit={hit}
              classNames={{
                root: 'line-clamp-3',
                highlighted: 'bg-lime-200',
              }}
            />
          ) : (
            <span className="line-clamp-3">{hit.title}</span>
          )}
        </Link>
      </div>

      <div className="px-2 pb-2">
        <PriceTag
          product={hit}
          centerUsualPrice={true}
          showFromPrefix={isCluster}
        />
      </div>

      <div className="flex justify-center pb-2">
        {isCluster ? (
          <Link
            to={internalURL(hit)}
            className="bg-ui hover:bg-green-500 rounded-full text-white text-sm h-8 px-4 flex items-center justify-center"
          >
            Σύγκριση τιμών ({countFormat.format(hit['member-count'])})
          </Link>
        ) : (
          <QuantityWidget
            addFun={() => basket.addOneItem(hit)}
            removeFun={() => basket.removeOneItem(hit)}
            quantity={basket?.products?.[hit.id]?.['zuper-basket-quantity']}
          />
        )}
      </div>
    </div>
  );
}

function BasketDeleteButton({ id }) {
  const basketContext = useContext(BasketContext);
  return (
    <span className="flex items-center justify-center rounded-full cursor-pointer select-none">
      <a
        className={
          'rounded-full flex items-center justify-center bg-ui hover:bg-green-400 text-sm text-white h-8 px-4'
        }
        onClick={() => basketContext.removeProduct(id)}
      >
        <span>Διαγραφή</span>
      </a>
    </span>
  );
}

function SubTotalPrice({ quantity, perItemPrice, loading = false }) {
  return (
    <span className="pb-4 font-bold">
      {loading ? (
        <HorizontalPreload short={true} />
      ) : (
        formatPrice({ price: quantity * perItemPrice })
      )}
    </span>
  );
}

export function MobileHit({
  hit,
  showMarketIcon = true,
  basket = false,
  priceLoading = false,
  useHighlight = true,
}) {
  const basketContext = useContext(BasketContext);
  const isCluster = isMultiMarketCluster(hit);
  const perItemPrice = hit.prices?.['per-item']?.scalar;
  const quantity =
    basketContext?.products?.[hit.id]?.['zuper-basket-quantity'] || 0;

  return (
    <div className="p-1 min-h-full hover:border-gray-400 flex items-center">
      <div className="flex-none w-24">
        {showMarketIcon && !isCluster && (
          <div className="absolute bg-white border border-gray-300 rounded-full shadow-xl w-8 h-8 flex items-center justify-center">
            <MarketIcon market={hit.market} />
          </div>
        )}
        <Link to={internalURL(hit)}>
          <img
            className={
              'object-contain w-24 h-24' +
              (hit.discontinued ? ' grayscale opacity-80' : '')
            }
            src={photoURL(hit)}
            onError={(e) => {
              e.target.src = noPhoto;
            }}
          />
        </Link>
      </div>
      <div className="flex-grow self-center pl-2">
        <div className="text-gray-700 text-sm px-2 pt-3">
          <Link to={internalURL(hit)} className="hover:underline">
            {basket || !useHighlight ? (
              <span className="line-clamp-3">{hit.title}</span>
            ) : (
              <Highlight
                attribute="title"
                hit={hit}
                classNames={{
                  root: 'line-clamp-3',
                  highlighted: 'bg-lime-200',
                }}
              />
            )}
          </Link>
        </div>
        {isCluster && (
          <div className="px-2 pt-1">
            <MarketIconsRow markets={hit.markets} />
          </div>
        )}
        <div className="p-2">
          {hit.discontinued ? (
            <span className="text-red-700 font-bold">
              Ο κωδικός έχει καταργηθεί, βρείτε εναλλακτικό προϊόν.
            </span>
          ) : (
            <PriceTag
              product={hit}
              boldPerItem={!basket}
              loading={priceLoading}
              showFromPrefix={isCluster}
            />
          )}
        </div>
      </div>
      <div className="flex-none flex flex-col items-center">
        {basket && !hit.discontinued && (
          <SubTotalPrice
            quantity={quantity}
            perItemPrice={perItemPrice}
            loading={priceLoading}
          />
        )}
        {hit.discontinued ? (
          <BasketDeleteButton id={hit.id} />
        ) : isCluster ? (
          <Link
            to={internalURL(hit)}
            className="rounded-full flex items-center justify-center bg-gray-300 hover:bg-gray-400 text-sm text-black h-8 px-4 text-center"
          >
            Σύγκριση
          </Link>
        ) : (
          <QuantityWidget
            addFun={() => basketContext.addOneItem(hit)}
            removeFun={() => basketContext.removeOneItem(hit)}
            quantity={quantity}
          />
        )}
      </div>
    </div>
  );
}
